import { Card } from '@/common/ui/Card/Card'
import { cn } from '@/common/lib/cn/cn'
import { ThresholdProgressBar, getThresholdVariant, thresholdTextClass } from '@/common/ui/ThresholdProgressBar'
import type { GenerationBlockAvgScoreSchema } from '@/common/api/client/types.gen'
import { BLOCK_TITLE_TEXT_CLASS, BLOCK_TOP_BORDER, DASHBOARD_BLOCK_TYPES } from '../lib/scoreColors'
import { getBlockTitle, getCriteriaLabel } from '../lib/labels'

type CompanyStatsCriteriaGridProps = {
    blocks: GenerationBlockAvgScoreSchema[]
}

const CRITERIA_ROW_GRID = 'grid-cols-[9rem_minmax(0,1fr)_2.25rem]'

export const CompanyStatsCriteriaGrid = ({ blocks }: CompanyStatsCriteriaGridProps) => {
    const dashboardBlocks = blocks.filter(b => DASHBOARD_BLOCK_TYPES.includes(b.block_type))

    if (dashboardBlocks.length === 0) return null

    return (
        <div className='flex flex-col gap-4'>
            <h3 className='text-xs font-semibold uppercase tracking-wide text-base-content/80'>
                Детализация по критериям — % присутствия в карточках
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'>
                {dashboardBlocks.map(block => {
                    const criterias = block.criterias ?? []
                    const topBorder = BLOCK_TOP_BORDER[block.block_type]
                    const sectionTitle = getBlockTitle(block.block_type, block.title)

                    return (
                        <Card key={block.block_type} color='primary' topBorderColor={topBorder} className='h-full'>
                            <h4
                                className={cn(
                                    'text-xs font-semibold mb-2 font-bold uppercase ',
                                    topBorder ? BLOCK_TITLE_TEXT_CLASS[topBorder] : 'text-base-content'
                                )}
                            >
                                {sectionTitle}
                            </h4>
                            <ul className='flex flex-col'>
                                {criterias.length === 0 ? (
                                    <li className='text-sm text-base-content/50'>Нет критериев</li>
                                ) : (
                                    criterias.map((criteria, index) => {
                                        const label = getCriteriaLabel(
                                            criteria.title,
                                            criteria.title_type,
                                            criteria.criteria_name
                                        )
                                        const percent = criteria.percent ?? 0
                                        const roundedPercent = Math.round(percent)
                                        const thresholdVariant = getThresholdVariant(percent)
                                        const rowKey = `${block.block_type}-${criteria.criteria_name}`
                                        const isLastRow = index === criterias.length - 1

                                        return (
                                            <li
                                                key={rowKey}
                                                className={cn(
                                                    'grid items-center gap-x-0.2 py-2 text-xs',
                                                    CRITERIA_ROW_GRID,
                                                    !isLastRow && 'border-b border-white/10'
                                                )}
                                            >
                                                <span className='text-base-content/80 leading-snug'>{label}</span>
                                                <ThresholdProgressBar
                                                    value={percent}
                                                    className='min-w-0'
                                                    aria-label={`${label}: ${roundedPercent}%`}
                                                />
                                                <span
                                                    className={cn(
                                                        'text-right font-medium tabular-nums',
                                                        thresholdTextClass[thresholdVariant]
                                                    )}
                                                >
                                                    {roundedPercent}%
                                                </span>
                                            </li>
                                        )
                                    })
                                )}
                            </ul>
                        </Card>
                    )
                })}
            </div>
        </div>
    )
}
